import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import GenericCrudServiceV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClient from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClient";
import WorkLog from "../models/WorkLog";
import environment from "../environment.json";
import OpsDateUtils from "../utils/OpsDateUtils";
import Holiday from "../models/Holiday";
import HolidayUtils from "../utils/HolidayUtils";
import WorkLogSummary from "../models/WorkLogSummary";
import WorkLogReport from "../models/WorkLogReport";
import ReportDTO from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import { ReportExtension } from "../types/ReportExtension";
import { WorklogExternalReportDTO } from "../models/WorklogExternalReportDTO";
import Timeoff from "../models/Timeoff";
import { WorklogMissingHours } from "../models/WorklogMissingHours";
import UnifiedReportDTO from "../models/UnifiedReportDTO";
import axios, { AxiosError } from "axios";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";

class WorkLogService extends GenericCrudServiceV2<WorkLog> {
  path: string;

  constructor(path: string) {
    super(path);
    this.path = path;
  }

  searchAll(
    page: number,
    pageSize: number,
    assignationExternalId: string,
    year: number,
    month: number,
    userExternalId: string | null | undefined,
    weekDay?: number,
  ): Promise<ApiResponseV2<PagedResult<WorkLog>>> {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page.toString());
    searchParams.set("pageSize", pageSize.toString());
    searchParams.set("assignationExternalId", assignationExternalId);
    searchParams.set("year", year.toString());
    searchParams.set("month", month.toString());

    if (userExternalId) {
      searchParams.set("userExternalId", userExternalId);
    }

    if (weekDay) {
      searchParams.set("weekDay", weekDay.toString());
    }

    const query = `${this.basePath}/filter?${searchParams.toString()}`;

    return HttpClientV2.get(query);
  }

  async getAllWithPrefilledEmptyPlaceholders(
    page: number,
    pageSize: number,
    assignationExternalId: string,
    year: number,
    month: number,
    userExternalId: string | null | undefined,
    holidays: Holiday[],
    weekDay?: number,
  ): Promise<PagedResult<WorkLog>> {
    const days = pageSize ?? OpsDateUtils.utcOffset(new Date(year, month + 1, 0)).getDate();

    const content = (await this.searchAll(page, days, assignationExternalId, year, month, userExternalId, weekDay)).getContentOrThrowError();
    const prefilledContent = this.fillEmptySlots(content, year, month, holidays);

    return Promise.resolve(PagedResult.fromArray(prefilledContent));
  }

  fillEmptySlots(content: PagedResult<WorkLog>, year: number, month: number, holidays: Holiday[]): WorkLog[] {
    const prefilledContent: WorkLog[] = [];
    const worklogs: WorkLog[] = content.content;
    const days = OpsDateUtils.utcOffset(new Date(year, month + 1, 0)).getDate();

    for (let i = 1; i <= days; i++) {
      const worklogDayUTC = OpsDateUtils.utcOffset(new Date(worklogs[0]?.shortDay));

      const alreadyCreatedWorklog: boolean = worklogs != null && worklogDayUTC.getDate() === i;

      if (alreadyCreatedWorklog) {
        let dailyWorklog = worklogs[0];
        const utcDate = OpsDateUtils.utcOffset(new Date(dailyWorklog.shortDay));

        dailyWorklog.isHoliday = HolidayUtils.isHoliday(utcDate, holidays);
        dailyWorklog.isWeekend = OpsDateUtils.isWeekend(utcDate);

        prefilledContent.push(dailyWorklog);
        worklogs.shift();
      } else {
        const utcDate = OpsDateUtils.utcOffset(new Date(year, month, i));

        prefilledContent.push({
          hours: 0,
          internalHours: 0,
          adjustmentHours: 0,
          bonusHours: 0,
          shortDay: utcDate,
          externalId: undefined,
          assignationExternalId: "",
          assignationName: "",
          isWeekend: OpsDateUtils.isWeekend(utcDate),
          isHoliday: HolidayUtils.isHoliday(utcDate, holidays),
        });
      }
    }

    return prefilledContent;
  }

  async downloadReport(url: string): Promise<any> {
    try {
      const response = await HttpClient.getFile(url);
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response && error.response.data instanceof Blob) {
        // If it's an AxiosError and there is a response with Blob data, read the Blob as a string
        const blobData = error.response.data;
        const errorMessage = await new Response(blobData).text();
        throw new Error(errorMessage);
      } else {
        throw error;
      }
    }
  }

  async downloadWeeklyHoursReport(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-weekly-hours-report?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadWeeklyHoursReportXLSX(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-weekly-hours-report-xlsx?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadDetailedDailyReport(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-daily-detail-report?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadDetailedDailyReportXLSX(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-daily-detail-report-xlsx?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadVacationReport(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-vacation-report?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadVacationReportXLSX(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-vacation-report-xlsx?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadMultimonthReportXLSX(project: string, year: number, month: number): Promise<any> {
    const url: string = `${environment.baseURL}${this.basePath}/download-multimonth-report-xlsx?year=${year}&month=${month}&projectExternalId=${project}`;
    return await this.downloadReport(url);
  }

  async downloadReportWorkLog(
    assignation: string,
    year: number,
    month: number,
    unified: boolean = false,
    format: ReportExtension,
    userExternalId?: string,
  ): Promise<any> {
    let url: string = `${environment.baseURL}${this.basePath}/download-worklog`;
    let params: string = `?year=${year}&month=${month}&assignationExternalId=${assignation}&unified=${unified}&format=${format}`;

    if (userExternalId) {
      url += `/${userExternalId}`;
    }

    return (await HttpClient.getFileWithErrorMessage(url + params)).data;
  }

  async createTimeOffWorklogs(wl: WorkLog, from: Date, to: Date, half: boolean, userExternalId: string): Promise<ApiResponseV2<WorkLog[]>> {
    return HttpClientV2.post(`${environment.baseURL}${this.basePath}/timeOff-worklogs/create/${userExternalId}`, {
      baseWorklog: wl,
      startDate: from,
      endDate: to,
      halfDay: half,
    });
  }

  async deleteTimeOffWorklogs(from: Date, to: Date, userExternalId: string): Promise<ApiResponseV2<WorkLog[]>> {
    return HttpClientV2.post(`${environment.baseURL}${this.basePath}/timeOff-worklogs/delete/${userExternalId}`, {
      startDate: from,
      endDate: to,
    });
  }

  getMissingHours(): Promise<ApiResponseV2<WorklogMissingHours[]>> {
    let query = `${this.basePath}/get-missing-hours-view`;
    return HttpClientV2.get(query);
  }

  findWorklogsSummary(
    page: number,
    pageSize: number,
    year: number,
    month: number,
    projectExternalId: string,
    weekDay?: number,
  ): Promise<ApiResponseV2<PagedResult<WorkLogSummary>>> {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page.toString());
    searchParams.set("pageSize", pageSize.toString());
    searchParams.set("year", year.toString());
    searchParams.set("month", month.toString());
    searchParams.set("projectExternalId", projectExternalId);
    if (weekDay) {
      searchParams.set("weekDay", weekDay.toString());
    }
    const query = `${this.basePath}/worklogs-summary?${searchParams.toString()}`;
    return HttpClientV2.get(query);
  }

  getWorkLogsPerWeek(project: string, year: number, month: number): Promise<ApiResponseV2<ReportDTO<WorkLogReport>>> {
    return HttpClientV2.get(`${this.basePath}/weekly-hours?year=${year}&month=${month}&projectExternalId=${project}`);
  }

  getWorklogExternalReport(worklogExternalId: string): Promise<ApiResponseV2<WorklogExternalReportDTO>> {
    return HttpClientV2.get(`${this.basePath}/external-report/${worklogExternalId}`);
  }

  sendInternalReportToSlack(message: string, workLogExternalId: string  ): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${environment.baseURL}${this.basePath}/${workLogExternalId}/send-internal-report`, { data: message });
  }

  sendExternalReportToSlack(message: string, workLogExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${environment.baseURL}${this.basePath}/${workLogExternalId}/send-external-report`, { data: message });
  }

  findTimeOffsByRequesterAndDate(assignationExternalId: string, year: number, month: number): Promise<ApiResponseV2<Timeoff[]>> {
    return HttpClientV2.get(`ensolvers-ops/timeoff/getTimeOffs?assignationExternalId=${assignationExternalId}&year=${year}&month=${month}`);
  }

  findWorkLogsByRequesterAndDates(userExternalId: string, startDate: string, endDate: string): Promise<ApiResponseV2<WorkLog[]>> {
    return HttpClientV2.get(`${environment.baseURL}${this.basePath}/getWorkLogs/${userExternalId}?startDate=${startDate}&endDate=${endDate}`);
  }

  findMonthWorkLogsByAssignationId(assignationExternalId: string, month: number): Promise<ApiResponseV2<WorkLog[]>> {
    return HttpClientV2.get(`${environment.baseURL}${this.basePath}/assignation-workLogs/${assignationExternalId}?month=${month}`);
  }

  findByPositionExternalId(positionExternalId: string): Promise<ApiResponseV2<WorkLog[]>> {
    return HttpClientV2.get(`${environment.baseURL}${this.basePath}/find-by-position?positionExternalId=${positionExternalId}`);
  }

  adjustHours(
    assignationExternalId: string,
    userExternalId: string,
    year: number,
    month: number,
    workReportDTO: WorkLogReport,
  ): Promise<ApiResponseV2<string>> {
    return HttpClientV2.put(`${environment.baseURL}${this.basePath}/adjust-hours`, {
      assignationExternalId,
      userExternalId,
      year,
      month,
      missingHours: workReportDTO.missingHours,
      adjustedHours: workReportDTO.adjustedHours,
    });
  }

  getUnifiedReports({
    userExternalId,
    month,
    year,
  }: {
    userExternalId: string;
    month: number;
    year: number;
  }): Promise<ApiResponseV2<PagedResult<UnifiedReportDTO>>> {
    return HttpClientV2.get(
      `${environment.baseURL}${this.basePath}/unified-reports?userExternalId=${userExternalId}&month=${month}&year=${year}`,
    );
  }

  getTotalInternalHours(year: number, month: number): Promise<ApiResponseV2<number>> {
    const searchParams = new URLSearchParams();
    searchParams.set("year", year.toString());
    searchParams.set("month", month.toString());

    const URL = `${environment.baseURL}${this.basePath}/total-internal-hours-for-month?${searchParams.toString()}`;
    return HttpClientV2.get(URL);
  }
}

export default new WorkLogService("/ensolvers-ops/work-log");
